import { useLocation } from 'wouter';
import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook to sync active tab with URL hash
 *
 * @param defaultTab - The default tab when no hash is present
 * @returns [activeTab, setActiveTab] tuple similar to useState
 *
 * @example
 * const [activeTab, setActiveTab] = useTabState('overview');
 *
 * // URL: /accounts/123#structure
 * // Refreshing page shows Structure tab active
 */
export function useTabState(defaultTab: string): [string, (tab: string) => void] {
  const [location, setLocation] = useLocation();

  const [activeTab, setActiveTabState] = useState(() => {
    // Initialize from URL hash — use first segment only to support nested hashes
    // e.g. "#accounts/acc-123/overview" → "accounts"
    const hash = window.location.hash.replace('#', '').split('/')[0];
    return hash || defaultTab;
  });

  // Listen for hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '').split('/')[0];
      setActiveTabState(hash || defaultTab);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [defaultTab]);

  const setActiveTab = useCallback(
    (tab: string) => {
      setActiveTabState(tab);
      // Update URL hash
      const pathname = location.split('?')[0].split('#')[0];
      const search = location.split('?')[1]?.split('#')[0] || '';
      setLocation(`${pathname}${search ? `?${search}` : ''}#${tab}`, { replace: true });
    },
    [setLocation, location]
  );

  return [activeTab, setActiveTab];
}
